import { useState } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/ui/date-picker';
import { Search, Plus, Eye, Edit, Trash2, Building2 } from 'lucide-react';
import { PlatformConsoleBreadcrumb } from '@/components/platform_console/PlatformConsoleBreadcrumb';
import { PaginatedTableControls } from '@/components/ui/paginated-table';

interface Document {
  id: number;
  name: string;
  description: string;
  status: string;
  vectorized_status: string;
  update_cadence: string;
  expiration_date?: string;
}

interface DocumentsIndexProps {
  documents: Document[];
  current_page: number;
  total_pages: number;
  per_page: number;
  total_count: number;
  search?: string;
  sort?: string;
}

export default function DocumentsIndex({
  documents,
  current_page,
  total_pages,
  per_page,
  total_count,
  search = '',
  sort = ''
}: DocumentsIndexProps) {
  const [searchQuery, setSearchQuery] = useState(search);
  const [sortBy, setSortBy] = useState(sort || 'recent');

  const getStatusBadge = (status: string) => {
    const variants = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      processing: 'bg-blue-100 text-blue-800'
    } as const;

    return variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800';
  };

  const handleSearch = () => {
    router.get('/documents', { search: searchQuery, sort: sortBy, page: 1, per_page: per_page }, { preserveState: true });
  };

  const handleSortChange = (newSort: string) => {
    setSortBy(newSort);
    router.get('/documents', { search: searchQuery, sort: newSort, page: 1, per_page: per_page }, { preserveState: true });
  };

  const handleDelete = (id: number) => {
    if (confirm('Are you sure?')) {
      router.delete(`/documents/${id}`);
    }
  };

  const updateField = async (id: number, payload: Record<string, unknown>) => {
    try {
      const response = await fetch(`/documents/${id}.json`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        // Update local state optimistically
        const updatedDocuments = documents.map(doc =>
          doc.id === id ? { ...doc, ...data.document } : doc
        );
        // Trigger a re-render by updating the documents array
        router.reload({ only: ['documents'] });
      }
    } catch (e) {
      console.error('Failed to update document', e);
    }
  };

  return (
    <>
      <Head title="Documents" />

      <div className="container mx-auto p-6 max-w-7xl">
        <div className="space-y-6">
          <PlatformConsoleBreadcrumb section="document_pipeline" current="Documents" />
          <h1 className="text-2xl font-semibold">Documents</h1>

          {/* Search and Filter Bar */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-4 items-center flex-1">
                  <div className="flex gap-2 flex-1 max-w-md">
                    <Input
                      placeholder="Search documents..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="flex-1"
                    />
                    <Button onClick={handleSearch} variant="outline">
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>

                  <Select value={sortBy} onValueChange={handleSortChange}>
                    <SelectTrigger className="w-[210px]">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recent">Most Recently Created</SelectItem>
                      <SelectItem value="alpha">Alphabetical</SelectItem>
                      <SelectItem value="vectorized">Last Vectorized</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2">
                  <Button asChild variant="outline">
                    <Link href="/municipality_documents">
                      <Building2 className="h-4 w-4 mr-2" />
                      Manage Municipalities
                    </Link>
                  </Button>
                  <Button asChild variant="outline">
                    <Link href="/documents/mass_upload">
                      <Plus className="h-4 w-4 mr-2" />
                      Mass Upload
                    </Link>
                  </Button>
                  <Button asChild variant="outline">
                    <Link href="/demo-documents/new">
                      <Plus className="h-4 w-4 mr-2" />
                      Demo Document
                    </Link>
                  </Button>
                  <Button asChild>
                    <Link href="/documents/new">
                      <Plus className="h-4 w-4 mr-2" />
                      New Document
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documents Table */}
          <Card>
            <CardContent className="p-0">
              {documents.length > 0 ? (
                <>
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-blue-500 hover:bg-blue-500">
                          <TableHead className="text-white font-semibold">Name</TableHead>
                          <TableHead className="text-white font-semibold">Description</TableHead>
                          <TableHead className="text-white font-semibold">Status</TableHead>
                          <TableHead className="text-white font-semibold">Cadence</TableHead>
                          <TableHead className="text-white font-semibold">Expiration</TableHead>
                          <TableHead className="text-white font-semibold">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {documents.map((document) => (
                          <TableRow key={document.id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <span className="truncate max-w-[300px]" title={document.name}>{document.name}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="truncate max-w-[150px] block">
                                {document.description}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge className={getStatusBadge(document.status)}>
                                {document.status.charAt(0).toUpperCase() + document.status.slice(1)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <select
                                className="border rounded-md px-3 py-1 text-sm shadow-sm bg-white min-w-[120px]"
                                value={document.update_cadence}
                                onChange={(e) => {
                                  const newCadence = e.target.value;
                                  updateField(document.id, { update_cadence: newCadence });
                                }}
                              >
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                                <option value="quarterly">Quarterly</option>
                                <option value="yearly">Yearly</option>
                              </select>
                            </TableCell>
                            <TableCell>
                              <DatePicker
                                value={document.expiration_date ? new Date(document.expiration_date) : undefined}
                                onChange={(date) => updateField(document.id, { expiration_date: date?.toISOString().slice(0, 10) })}
                                placeholder="Set expiration"
                                className="min-w-[140px] h-8 text-sm"
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button asChild variant="outline" size="sm">
                                  <Link href={`/documents/${document.id}`}>
                                    <Eye className="h-4 w-4" />
                                  </Link>
                                </Button>
                                <Button asChild variant="outline" size="sm">
                                  <Link href={`/documents/${document.id}/edit`}>
                                    <Edit className="h-4 w-4" />
                                  </Link>
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDelete(document.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile Card Layout */}
                  <div className="md:hidden space-y-4 p-4">
                  {documents.map((document) => (
                    <Card key={document.id}>
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          <div>
                            <div className="font-semibold text-sm text-blue-600 mb-1">Name</div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium" title={document.name}>{document.name}</span>
                            </div>
                          </div>

                          <div>
                            <div className="font-semibold text-sm text-blue-600 mb-1">Description</div>
                            <div>{document.description}</div>
                          </div>

                          <div>
                            <div className="font-semibold text-sm text-blue-600 mb-1">Status</div>
                            <Badge className={getStatusBadge(document.status)}>
                              {document.status.charAt(0).toUpperCase() + document.status.slice(1)}
                            </Badge>
                          </div>

                          <div>
                            <div className="font-semibold text-sm text-blue-600 mb-1">Cadence</div>
                            <select
                              className="border rounded-md px-3 py-2 text-sm w-full shadow-sm"
                              value={document.update_cadence}
                              onChange={(e) => {
                                const newCadence = e.target.value;
                                updateField(document.id, { update_cadence: newCadence });
                              }}
                            >
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                              <option value="quarterly">Quarterly</option>
                              <option value="yearly">Yearly</option>
                            </select>
                          </div>

                          <div>
                            <div className="font-semibold text-sm text-blue-600 mb-1">Expiration</div>
                            <DatePicker
                              value={document.expiration_date ? new Date(document.expiration_date) : undefined}
                              onChange={(date) => updateField(document.id, { expiration_date: date?.toISOString().slice(0, 10) })}
                              placeholder="Set expiration"
                              className="w-full"
                            />
                          </div>

                          <div>
                            <div className="font-semibold text-sm text-blue-600 mb-2">Actions</div>
                            <div className="flex flex-col gap-2">
                              <Button asChild variant="outline" size="sm">
                                <Link href={`/documents/${document.id}`}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  View
                                </Link>
                              </Button>
                              <Button asChild variant="outline" size="sm">
                                <Link href={`/documents/${document.id}/edit`}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </Link>
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDelete(document.id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <div className="text-muted-foreground mb-4">
                    No documents found.{' '}
                    <Link href="/documents/new" className="text-blue-600 hover:underline">
                      Create a new document
                    </Link>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <PaginatedTableControls
            currentPage={current_page}
            totalPages={total_pages}
            perPage={per_page}
            totalCount={total_count}
            baseUrl="/documents"
          />
        </div>
      </div>
    </>
  );
}
